import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const TILES = [
  {
    key: "allocations",
    icon: "bi-laptop-fill",
    label: "My Assets",
    sub: "View all assets assigned to you",
    color: "#3d4c7a", bg: "#eef0f8",
    tab: "allocations",
  },
  {
    key: "new-request",
    icon: "bi-plus-circle-fill",
    label: " Asset Request",
    sub: "Submit a new asset request",
    color: "#16a34a", bg: "#f0fdf4",
    tab: "new-request",
  },
  {
    key: "requests",
    icon: "bi-clock-history",
    label: "My Requests",
    sub: "Track your submitted requests",
    color: "#d97706", bg: "#fffbeb",
    tab: "requests",
  },
];

export default function EmpAssetLanding() {
  const navigate   = useNavigate();
  const token       = localStorage.getItem("token");
  const employeeId  = localStorage.getItem("employeeId");

  const [allocations, setAllocations] = useState([]);
  const [requests,    setRequests]    = useState([]);
  const [employee,    setEmployee]    = useState(null);
  const [loading,     setLoading]     = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, allocRes, reqRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/employees/${employeeId}`,                       { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/asset-allocations/employee/${employeeId}`,      { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE_URL}/api/asset-requests/employee/${employeeId}`,         { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (empRes.ok)   setEmployee(await empRes.json());
      if (allocRes.ok) { const d = await allocRes.json(); setAllocations(Array.isArray(d) ? d : d.content || []); }
      if (reqRes.ok)   { const d = await reqRes.json();   setRequests(Array.isArray(d) ? d : []); }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token, employeeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const s = document.createElement("style");
    s.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
      .eal-root * { box-sizing: border-box; }
      .eal-root { font-family: 'Sora', sans-serif; background: #f4f6fb; min-height: 100vh; color: #1a1f36; }
      .eal-tile { background:#fff; border:1.5px solid #e8eaf0; border-radius:18px; padding:36px 28px;
        display:flex; flex-direction:column; align-items:flex-start; gap:16px; cursor:pointer;
        transition:all 0.2s ease; flex:1; min-width:240px; }
      .eal-tile:hover { border-color:#080235; box-shadow:0 10px 32px rgba(8,2,53,0.12); transform:translateY(-4px); }
      .eal-tile-icon { width:60px; height:60px; border-radius:16px; display:flex; align-items:center; justify-content:center; font-size:26px; }
      .eal-stat { background:#fff; border:1px solid #e8eaf0; border-radius:14px; padding:20px 24px;
        display:flex; align-items:center; gap:16px; flex:1; min-width:150px; }
      @keyframes spin { to { transform:rotate(360deg); } }
      .eal-spin { width:28px; height:28px; border-radius:50%; border:3px solid #e8eaf0; border-top-color:#080235; animation:spin 0.7s linear infinite; margin:80px auto; }
      @keyframes fadeUp { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
      .eal-animate { animation: fadeUp 0.3s ease both; }
    `;
    document.head.appendChild(s);
    const ic = document.createElement("link");
    ic.rel = "stylesheet";
    ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(ic);
    return () => { s.remove(); ic.remove(); };
  }, []);

  const activeAssets  = allocations.filter(a => a.status === "ACTIVE").length;
  const pendingReqs   = requests.filter(r => (r.status || "").toLowerCase() === "pending").length;
  const approvedReqs  = requests.filter(r => (r.status || "").toLowerCase() === "approved").length;

  const STATS = [
    { icon: "bi-laptop-fill",       label: "Active Assets",   val: activeAssets, iconBg: "#eef0f8", iconColor: "#3d4c7a" },
    { icon: "bi-hourglass-split",   label: "Pending",         val: pendingReqs,  iconBg: "#fffbeb", iconColor: "#d97706" },
    { icon: "bi-check-circle-fill", label: "Approved",        val: approvedReqs, iconBg: "#f0fdf4", iconColor: "#16a34a" },
  ];

  const tileCount = { allocations: activeAssets, "new-request": null, requests: requests.length };

  return (
    <div className="eal-root">
      <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <AssetModuleNavbar />
      </div>

      <div style={{ padding: "24px 32px 48px", paddingTop: 130 }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: "#080235", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className="bi bi-laptop-fill" style={{ color: "#fff", fontSize: 22 }} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#080235" }}>My Asset Portal</h1>
            {employee && (
              <p style={{ margin: 0, fontSize: 13, color: "#8892a4", marginTop: 2 }}>
                {employee.firstName} {employee.lastName}
                <span style={{ margin: "0 6px", color: "#d0d4df" }}>·</span>
                {employee.designation || "—"}
                <span style={{ margin: "0 6px", color: "#d0d4df" }}>·</span>
                {employee.department?.name || employee.department || "—"}
              </p>
            )}
          </div>
        </div>

        {loading ? <div className="eal-spin" /> : (
          <div className="eal-animate">
            {/* Stats */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 36 }}>
              {STATS.map(({ icon, label, val, iconBg, iconColor }) => (
                <div className="eal-stat" key={label}>
                  <div style={{ width: 44, height: 44, borderRadius: 11, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <i className={`bi ${icon}`} style={{ color: iconColor, fontSize: 20 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, color: "#1a1f36" }}>{val}</div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.5px", marginTop: 3 }}>{label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Action Tiles */}
            <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#8892a4", marginBottom: 16 }}>What would you like to do?</p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {TILES.map(({ key, icon, label, sub, color, bg, tab }) => (
                <div key={key} className="eal-tile" onClick={() => navigate("/myassets", { state: { tab } })}>
                  <div className="eal-tile-icon" style={{ background: bg }}>
                    <i className={`bi ${icon}`} style={{ color }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1f36", marginBottom: 4 }}>{label}</div>
                    <div style={{ fontSize: 12.5, color: "#8892a4", lineHeight: 1.5 }}>{sub}</div>
                  </div>
                  {tileCount[key] !== null && (
                    <div style={{ marginTop: "auto", fontSize: 22, fontWeight: 700, color }}>
                      {tileCount[key]}
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color, fontWeight: 600 }}>
                    Go <i className="bi bi-arrow-right" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
